import plur from 'plur';
import {
  Banner,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Stack,
  Strong,
} from '@customerio/pluma-components/react';
import { formatNumber } from '@/utils/number';
import type { MergeSummary } from '../types';

type MergeSummaryPanelProps = {
  /** The preview summary; absent while the preview is unavailable. */
  summary: MergeSummary | undefined;
  /** The secondary person's display name. */
  secondaryDisplayName: string;
  /** The workspace's word for several people ("profiles" once renamed). */
  peopleTerm: string;
  onRequestFeedback: () => void;
};

function addedAttributesLabel(added: number | undefined): string {
  if (added == null || added === 0) {
    return 'No attributes';
  }
  return `${formatNumber(added)} ${plur('attribute', added)}`;
}

/** What the merge will do, plus the invitation to ask for other merging options. */
export function MergeSummaryPanel({
  summary,
  secondaryDisplayName,
  peopleTerm,
  onRequestFeedback,
}: MergeSummaryPanelProps) {
  return (
    <Panel withSectionBorders>
      <PanelHeader>
        <PanelTitle level="3">Summary</PanelTitle>
      </PanelHeader>
      <PanelSection withBorder={false}>
        <Stack gap="200">
          <Paragraph data-test="people-merge-summary-attributes">
            <Strong>{addedAttributesLabel(summary?.attributes.added)}</Strong> will be added.
          </Paragraph>
          <Paragraph>
            <Strong>All deliveries</Strong> associated with both {peopleTerm} will be saved.
          </Paragraph>
          <Paragraph>
            <Strong>All unique relationships</Strong> associated with both {peopleTerm} will be saved.
          </Paragraph>
          <Paragraph>
            <Strong>Events received more than 29 days ago</Strong> associated with {secondaryDisplayName} may not be
            merged immediately.
          </Paragraph>
        </Stack>
      </PanelSection>
      <PanelSection withPadding={false} withBorder={false}>
        <Banner
          variant="information"
          title="Would you like other merging options?"
          action={{ label: 'Let us know', onClick: onRequestFeedback }}
          withResponsiveContainer={false}
          alignItems="center"
        />
      </PanelSection>
    </Panel>
  );
}
